import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelectTags } from '@/components/ui/multi-select-tags';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { codebookApi } from '@/lib/api/codebook';
import { profileApi } from '@/lib/api/profile';
import type {
  CreateCaseStudyDto,
  CaseStudyDto,
  UpdateCaseStudyDto,
  CaseStudyAttachmentDto,
  CaseStudyDraftFromFileDto,
} from '@paladin-monorepo/common';
import type { LookupItemDto, TeamMemberDto } from '@paladin-monorepo/common';
import { CaseStudyAttachments, uploadPendingCaseStudyFiles } from '@/components/profile/CaseStudyAttachments';

const validationSchema: yup.ObjectSchema<CreateCaseStudyDto> = yup.object({
  title: yup
    .string()
    .required('Title is required')
    .max(255, 'Title must be at most 255 characters'),
  description: yup.string().required('Description is required'),
  industryId: yup.string().required('Industry is required'),
  expertiseIds: yup.array().of(yup.string().required()),
  authorIds: yup.array().of(yup.string().required()),
});

interface CaseStudyFormProps {
  onSuccess?: (caseStudy: CaseStudyDto) => void;
  onSubmit: (
    data: CreateCaseStudyDto | UpdateCaseStudyDto
  ) => Promise<CaseStudyDto>;
  isSubmitting?: boolean;
  initialData?: CaseStudyDto;
  /** Prefill for create mode (e.g. LLM draft from uploaded file). Ignored when initialData is set. */
  draftValues?: CaseStudyDraftFromFileDto | null;
  initialPendingFiles?: File[];
  onCancel?: () => void;
  embedded?: boolean;
}

function formValuesFromProps(
  initialData?: CaseStudyDto,
  draftValues?: CaseStudyDraftFromFileDto | null,
): CreateCaseStudyDto {
  if (initialData) {
    return {
      title: initialData.title || '',
      description: initialData.description || '',
      industryId: initialData.industry?.id || '',
      expertiseIds: initialData.expertises?.map((e) => e.id) || [],
      authorIds: initialData.authors?.map((a) => a.id) || [],
    };
  }
  return {
    title: draftValues?.title || '',
    description: draftValues?.description || '',
    industryId: draftValues?.industryId || '',
    expertiseIds: draftValues?.expertiseIds || [],
    authorIds: [],
  };
}

export const CaseStudyForm = ({
  onSuccess,
  onSubmit,
  isSubmitting = false,
  initialData,
  draftValues,
  initialPendingFiles = [],
  onCancel,
  embedded = false,
}: CaseStudyFormProps) => {
  const { t } = useTranslation();
  const [industries, setIndustries] = useState<LookupItemDto[]>([]);
  const [expertises, setExpertises] = useState<LookupItemDto[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savedCaseStudy, setSavedCaseStudy] = useState<CaseStudyDto | null>(initialData ?? null);
  const [attachments, setAttachments] = useState<CaseStudyAttachmentDto[]>(
    initialData?.attachments ?? [],
  );
  const [pendingFiles, setPendingFiles] = useState<File[]>(
    initialData ? [] : initialPendingFiles,
  );

  const isEditMode = !!savedCaseStudy?.id;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<CreateCaseStudyDto>({
    resolver: yupResolver(validationSchema),
    defaultValues: formValuesFromProps(initialData, draftValues),
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [industriesData, expertisesData, teamData] = await Promise.all([
          codebookApi.listIndustries(),
          codebookApi.listExpertise(),
          profileApi.getTeam(),
        ]);
        setIndustries(industriesData);
        setExpertises(expertisesData);
        setTeamMembers(teamData);
      } catch (error) {
        console.error('Failed to fetch form options:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // Reset form when initialData / draftValues change (switching between create/edit mode)
  useEffect(() => {
    reset(formValuesFromProps(initialData, draftValues));
    setSavedCaseStudy(initialData ?? null);
    setAttachments(initialData?.attachments ?? []);
    setPendingFiles(initialData ? [] : initialPendingFiles);
  }, [initialData, draftValues, initialPendingFiles, reset]);

  const handleFormSubmit = async (data: CreateCaseStudyDto) => {
    try {
      const result = await onSubmit(data);
      setSavedCaseStudy(result);

      let nextAttachments = result.attachments ?? [];
      if (pendingFiles.length > 0) {
        try {
          const uploaded = await uploadPendingCaseStudyFiles(result.id, pendingFiles);
          nextAttachments = [...nextAttachments, ...uploaded];
          setPendingFiles([]);
        } catch {
          toast.error(t('profile.caseStudies.attachments.uploadError'));
        }
      }
      setAttachments(nextAttachments);

      if (!isEditMode) {
        reset({
          title: result.title,
          description: result.description,
          industryId: result.industry.id,
          expertiseIds: result.expertises.map((e) => e.id),
          authorIds: result.authors.map((a) => a.id),
        });
      }
      onSuccess?.({ ...result, attachments: nextAttachments });
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? 'update' : 'create'} case study:`,
        error
      );
    }
  };

  const expertiseIds = watch('expertiseIds') || [];
  const authorIds = watch('authorIds') || [];

  if (isLoading) {
    const loadingContent = (
      <p className="text-sm text-muted-foreground">{t('generic.loading')}</p>
    );

    if (embedded) {
      return loadingContent;
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.caseStudies.createNew')}</CardTitle>
        </CardHeader>
        <CardContent>{loadingContent}</CardContent>
      </Card>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              {t('profile.caseStudies.form.title.label')} *
            </Label>
            <Input
              id="title"
              placeholder={t('profile.caseStudies.form.title.placeholder')}
              {...register('title')}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              {t('profile.caseStudies.form.description.label')} *
            </Label>
            <Textarea
              id="description"
              rows={3}
              placeholder={t(
                'profile.caseStudies.form.description.placeholder'
              )}
              {...register('description')}
            />
            {errors.description && (
              <p className="text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Industry */}
          <div className="space-y-2">
            <Label htmlFor="industryId">
              {t('profile.caseStudies.form.industry.label')} *
            </Label>
            <Controller
              name="industryId"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="industryId">
                    <SelectValue
                      placeholder={t(
                        'profile.caseStudies.form.industry.placeholder'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry.id} value={industry.id}>
                        {t(`projects.industry.options.${industry.id}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.industryId && (
              <p className="text-sm text-red-600">
                {errors.industryId.message}
              </p>
            )}
          </div>

          {/* Expertises */}
          <div className="space-y-2">
            <Label>{t('profile.caseStudies.form.expertises.label')}</Label>
            <MultiSelectTags
              label={t('profile.caseStudies.form.expertises.label')}
              value={expertiseIds}
              onChange={(values) => setValue('expertiseIds', values)}
              options={expertises.map((e) => ({
                id: e.id,
                name: t(`projects.expertise.options.${e.id}`, { defaultValue: e.name ?? e.id }),
              }))}
              placeholder={t('profile.caseStudies.form.expertises.placeholder')}
              filterable
              allowCreate
              onCreateOption={(label) => {
                const trimmed = label.trim();
                if (!trimmed) return null;
                const existing = expertises.find(
                  (item) =>
                    item.id === trimmed ||
                    (item.name ?? item.id).toLowerCase() === trimmed.toLowerCase(),
                );
                if (existing) {
                  if (!expertiseIds.includes(existing.id)) {
                    setValue('expertiseIds', [...expertiseIds, existing.id]);
                  }
                  return null;
                }
                setExpertises((prev) => [...prev, { id: trimmed, name: trimmed }]);
                return { id: trimmed, name: trimmed };
              }}
              createOptionText={(label) =>
                t('profile.caseStudies.form.expertises.createOption', { label })
              }
              searchPlaceholder={t('profile.multiSelect.searchPlaceholder')}
              noResultsText={t('profile.multiSelect.noResults')}
              modal
            />
          </div>

          {/* Authors */}
          <div className="space-y-2">
            <Label>{t('profile.caseStudies.form.authors.label')}</Label>
            <MultiSelectTags
              label={t('profile.caseStudies.form.authors.label')}
              value={authorIds}
              onChange={(values) => setValue('authorIds', values)}
              options={teamMembers.map((m) => ({
                id: m.id,
                name: `${m.firstName} ${m.lastName}`,
              }))}
              placeholder={t('profile.caseStudies.form.authors.placeholder')}
              modal
            />
          </div>

          <CaseStudyAttachments
            caseStudyId={savedCaseStudy?.id}
            attachments={attachments}
            pendingFiles={pendingFiles}
            onAttachmentsChange={setAttachments}
            onPendingFilesChange={setPendingFiles}
          />

      {/* Submit and Cancel Buttons */}
      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('generic.cancel')}
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? isEditMode
              ? t('generic.saving')
              : t('generic.creating')
            : isEditMode
            ? t('generic.save')
            : t('generic.add')}
        </Button>
      </div>
    </form>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {isEditMode
            ? t('profile.caseStudies.edit.title') + ` ${savedCaseStudy?.title ?? initialData?.title ?? ''}`
            : t('profile.caseStudies.add.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>{formContent}</CardContent>
    </Card>
  );
};
